import type { SolanaNetworkConfig } from "../../config/solana";

export interface SolanaFeeRequest {
  network: string;
  programId: string;
  kind: "withdraw" | "withdraw_batch";
  mint: string | null;
  treeIndex: number;
  inputCount: number;
  outputCount: number;
  recipients: string[];
}
export interface SolanaFeeQuote {
  version: 1;
  request: SolanaFeeRequest;
  feeRecipient: string;
  issuedAt: number;
  expiresAt: number;
  fee: string;
  networkFee: string;
  accountRent: string;
  relayerMargin: string;
  networkFeeLamports: string;
  accountRentLamports: string;
  refundableRentLamports: string;
  transactions: number;
  mode: "inline" | "staged";
  newTokenAccounts: number;
}
/** Local approval context. Note identities never leave the browser for pricing. */
export interface SolanaWithdrawalQuote {
  quote: SolanaFeeQuote;
  selectedCommitments: string[];
  amount: bigint;
  inputTotal: bigint;
}

const uint64 = (v: unknown): v is string =>
  typeof v === "string" &&
  /^(0|[1-9][0-9]{0,19})$/.test(v) &&
  BigInt(v) < 1n << 64n;
export function validateSolanaFeeQuote(
  value: unknown,
  request: SolanaFeeRequest,
  now = Date.now(),
): SolanaFeeQuote {
  const q = value as SolanaFeeQuote;
  const r = q?.request;
  if (
    !q ||
    q.version !== 1 ||
    !r ||
    [
      "network",
      "programId",
      "kind",
      "mint",
      "treeIndex",
      "inputCount",
      "outputCount",
    ].some(
      (key) =>
        r[key as keyof SolanaFeeRequest] !==
        request[key as keyof SolanaFeeRequest],
    ) ||
    JSON.stringify(r.recipients) !== JSON.stringify(request.recipients) ||
    !Number.isSafeInteger(q.issuedAt) ||
    !Number.isSafeInteger(q.expiresAt) ||
    q.issuedAt > now + 5000 ||
    q.expiresAt <= now ||
    q.expiresAt - q.issuedAt > 60_000 ||
    ![
      q.fee,
      q.networkFee,
      q.accountRent,
      q.relayerMargin,
      q.networkFeeLamports,
      q.accountRentLamports,
      q.refundableRentLamports,
    ].every(uint64) ||
    BigInt(q.fee) !==
      BigInt(q.networkFee) + BigInt(q.accountRent) + BigInt(q.relayerMargin) ||
    BigInt(q.networkFeeLamports) <= 0n ||
    !Number.isInteger(q.transactions) ||
    q.transactions < 1 ||
    q.transactions > 100 ||
    !["inline", "staged"].includes(q.mode) ||
    typeof q.feeRecipient !== "string"
  )
    throw new Error(
      "Invalid or expired Solana fee quote. Refresh and review the fee again.",
    );
  if (
    request.mint === null &&
    (q.networkFee !== q.networkFeeLamports ||
      q.accountRent !== q.accountRentLamports)
  )
    throw new Error("Solana fee quote has inconsistent SOL costs.");
  return q;
}

export function assertSolanaWithdrawalQuote(
  approved: SolanaWithdrawalQuote | undefined,
  params: {
    network: SolanaNetworkConfig;
    mint: string | null;
    kind: SolanaFeeRequest["kind"];
    treeIndex: number;
    amount: bigint;
    recipients: string[];
    selectedCommitments: string[];
    inputTotal: bigint;
  },
) {
  if (!approved)
    throw new Error("Review a fresh Solana fee quote before withdrawing.");
  const fee = BigInt(approved.quote.fee);
  const gross =
    params.kind === "withdraw_batch" ? params.amount + fee : params.amount;
  validateSolanaFeeQuote(approved.quote, {
    network: params.network.key,
    programId: params.network.programId,
    kind: params.kind,
    mint: params.mint,
    treeIndex: params.treeIndex,
    inputCount: params.selectedCommitments.length,
    outputCount: params.inputTotal > gross ? 1 : 0,
    recipients: params.recipients,
  });
  if (
    approved.amount !== params.amount ||
    approved.inputTotal !== params.inputTotal ||
    gross > params.inputTotal ||
    JSON.stringify(approved.selectedCommitments) !==
      JSON.stringify(params.selectedCommitments) ||
    (params.kind === "withdraw" ? fee >= params.amount : fee > params.amount)
  )
    throw new Error("Withdrawal changed. Refresh and review its fee again.");
  return fee;
}
